/**
 * 2026 지방출자·출연기관 예산편성지침 (행안부)
 * IV. 예산구조 및 과목내역 — 사업예산·자본예산 시드
 *
 * - 사업예산 수입: 관 600 / 지출: 관 700 (+사업 예비비 800)
 * - 자본예산 수입: 관 100 / 지출: 관 200 (+자본 예비비 800 — 사업과 동일코드, budgetType으로 분리)
 */

import {
  clonePreset,
  validateStructure,
  getConfirmedStructure,
  loadStructurePayload,
  saveStructurePayload,
  loadSubjects,
  saveSubjects,
  STRUCTURE_BUDGET_TYPE,
  resolveBudgetTypes,
} from '../budgetStructureStore';
import { ensureSubjectsIdentity, normalizeMokSemokCode } from '../budgetSubjectHistoryStore';

export const MOFAS_2026_SOURCE =
  '행정안전부 「2026년도 지방출자·출연기관 예산편성지침」 IV.예산구조 및 과목내역';

const ATTR = {
  useYn: 'Y',
  eNaraDoum: '',
  funcDiv: '',
  natureDiv: '',
  fundUid: '',
  remark: '행안부 2026 지방출자·출연기관 예산편성지침',
};

/** 지출 공통 목·세목 (지침 §4 지출예산과목내역 — 주요) */
const EXPENSE_MOK_SEMOK = [
  {
    code: '101',
    name: '인건비',
    children: [
      { code: '101-01', name: '보수' },
      { code: '101-02', name: '기타직보수' },
      { code: '101-03', name: '무기계약근로자보수' },
      { code: '101-04', name: '기간제근로자등보수' },
    ],
  },
  { code: '107', name: '퇴직급여', children: [{ code: '107-01', name: '퇴직급여' }] },
  { code: '109', name: '평가급', children: [{ code: '109-01', name: '평가급' }] },
  {
    code: '201',
    name: '일반운영비',
    children: [
      { code: '201-01', name: '사무관리비' },
      { code: '201-02', name: '공공운영비' },
      { code: '201-03', name: '행사운영비' },
      { code: '201-12', name: '교육훈련비' },
      { code: '201-14', name: '회의비' },
      { code: '201-15', name: '복리후생비' },
    ],
  },
  { code: '202', name: '여비', children: [{ code: '202-01', name: '국내여비' }, { code: '202-02', name: '월액여비' }, { code: '202-03', name: '국외여비' }] },
  { code: '203', name: '업무추진비', children: [{ code: '203-01', name: '사업추진비' }, { code: '203-02', name: '기관운영업무추진비' }] },
  { code: '204', name: '직무수행경비', children: [{ code: '204-01', name: '직무수행경비' }] },
  { code: '206', name: '재료비', children: [{ code: '206-01', name: '재료비' }] },
  { code: '207', name: '연구개발비', children: [{ code: '207-01', name: '연구개발비' }] },
  { code: '214', name: '수선유지교체비', children: [{ code: '214-01', name: '수선유지교체비' }] },
  { code: '215', name: '동력비', children: [{ code: '215-01', name: '동력비' }] },
  { code: '217', name: '관서업무비', children: [{ code: '217-01', name: '관서업무비' }] },
  { code: '219', name: '감가상각비', children: [{ code: '219-01', name: '감가상각비' }] },
  { code: '220', name: '위탁관리비', children: [{ code: '220-01', name: '위탁관리비' }] },
  { code: '241', name: '개발대행사업비', children: [{ code: '241-01', name: '개발대행사업비' }] },
  { code: '301', name: '일반보상금', children: [{ code: '301-01', name: '일반보상금' }] },
  { code: '303', name: '포상금', children: [{ code: '303-01', name: '포상금' }] },
  { code: '304', name: '연금부담금등', children: [{ code: '304-01', name: '연금부담금등' }] },
  { code: '305', name: '배상금등', children: [{ code: '305-01', name: '배상금등' }] },
  { code: '306', name: '출연금', children: [{ code: '306-01', name: '출연금' }] },
  { code: '308', name: '자치단체등이전', children: [{ code: '308-01', name: '자치단체등이전' }] },
  { code: '309', name: '전출금', children: [{ code: '309-01', name: '전출금' }] },
  { code: '321', name: '민간사업지원금', children: [{ code: '321-01', name: '민간사업지원금' }] },
];

const SHARE_721 = { shareMokFrom: '721', remark: '721 세항 사용 목 계상 (지침 2-1)' };

/** 수입(사업수익) 트리 — 지침 2-1 */
const INCOME_TREE = {
  code: '600',
  name: '사업수익',
  children: [
    {
      code: '610',
      name: '영업수익',
      children: [
        { code: '611', name: '사용료수익', children: [{ code: '611-07', name: '시설사용료' }] },
        {
          code: '621',
          name: '상품등판매수익',
          children: [
            { code: '621-01', name: '상품판매수익' },
            { code: '621-02', name: '제품판매수익' },
            { code: '621-09', name: '기타재고자산판매수익' },
          ],
        },
        {
          code: '622',
          name: '관리사업수익',
          children: [
            { code: '622-01', name: '관리사업수익' },
            { code: '622-02', name: '관리사업부대수익' },
          ],
        },
        {
          code: '623',
          name: '대행사업수익',
          children: [
            { code: '623-01', name: '직접관리비수익' },
            { code: '623-02', name: '간접관리비수익' },
          ],
        },
        { code: '624', name: '입장료수익', children: [{ code: '624-01', name: '입장료수익' }] },
        {
          code: '627',
          name: '신용보증수익',
          children: [
            { code: '627-01', name: '보증료수익' },
            { code: '627-02', name: '예치금등이자' },
            { code: '627-03', name: '재보증료환입액' },
            { code: '627-09', name: '기타신용보증수익' },
          ],
        },
        {
          code: '635',
          name: '의료사업수익',
          children: [
            { code: '635-01', name: '입원수익' },
            { code: '635-02', name: '외래수익' },
            { code: '635-03', name: '수탁진료수익' },
            { code: '635-04', name: '기타의료수익' },
          ],
        },
        {
          code: '641',
          name: '용지및주택판매수익',
          children: [
            { code: '641-01', name: '택지판매수익' },
            { code: '641-03', name: '주택판매수익' },
            { code: '641-09', name: '기타용지등판매수익' },
          ],
        },
        {
          code: '642',
          name: '임대사업수익',
          children: [
            { code: '642-01', name: '주택및상가임대수익' },
            { code: '642-02', name: '용지임대수익' },
            { code: '642-09', name: '기타임대수익' },
          ],
        },
        {
          code: '646',
          name: '보조금수익',
          children: [
            { code: '646-01', name: '국비보조금수익' },
            { code: '646-02', name: '자치단체보조금수익' },
            { code: '646-03', name: '기타보조금수익' },
          ],
        },
        { code: '647', name: '기부금수익', children: [{ code: '647-01', name: '기부금수익' }] },
        {
          code: '648',
          name: '출연금수익',
          children: [
            { code: '648-01', name: '정부출연금수익' },
            { code: '648-02', name: '자치단체출연금수익' },
            { code: '648-09', name: '기타출연금수익' },
          ],
        },
        {
          code: '649',
          name: '후원금수익',
          children: [
            { code: '649-01', name: '지정후원금수익' },
            { code: '649-02', name: '비지정후원금수익' },
          ],
        },
        { code: '650', name: '출연금이자수익', children: [{ code: '650-01', name: '출연금이자수익' }] },
        { code: '651', name: '연구용역수입', children: [{ code: '651-01', name: '연구용역수익' }] },
        {
          code: '661',
          name: '기타영업수익',
          children: [
            { code: '661-02', name: '기타사용료수익' },
            { code: '661-03', name: '수수료수익' },
            { code: '661-04', name: '광고료수익' },
            { code: '661-05', name: '부대시설임대수익' },
            { code: '661-06', name: '회비수익' },
            { code: '661-09', name: '기타영업수익' },
          ],
        },
      ],
    },
    {
      code: '670',
      name: '영업외수익',
      children: [
        {
          code: '671',
          name: '이자수익',
          children: [
            { code: '671-01', name: '예금이자수익' },
            { code: '671-02', name: '대여금이자수익' },
            { code: '671-09', name: '기타이자수익' },
          ],
        },
        {
          code: '673',
          name: '정부보조금수익',
          children: [
            { code: '673-01', name: '국비보조금수익' },
            { code: '673-02', name: '자치단체보조금수익' },
            { code: '673-03', name: '출연금수익' },
            { code: '673-04', name: '기타보조금수익' },
          ],
        },
        { code: '674', name: '유형자산처분이익', children: [{ code: '674-01', name: '유형자산처분이익' }] },
        { code: '675', name: '외화환산이익', children: [{ code: '675-01', name: '외화환산이익' }] },
        { code: '676', name: '전기오류수정이익', children: [{ code: '676-01', name: '전기오류수정이익' }] },
        { code: '677', name: '채무면제이익', children: [{ code: '677-01', name: '채무면제이익' }] },
        { code: '678', name: '투자자산처분이익', children: [{ code: '678-01', name: '투자자산처분이익' }] },
        {
          code: '679',
          name: '기타영업외수익',
          children: [
            { code: '679-01', name: '기타임대수익' },
            { code: '679-02', name: '임대관리수익' },
            { code: '679-03', name: '불용품매각수익' },
            { code: '679-04', name: '변상금및위약금수익' },
            { code: '679-05', name: '기부금수익' },
            { code: '679-09', name: '기타영업외수익' },
          ],
        },
        { code: '683', name: '수탁사업수익', children: [{ code: '683-01', name: '수탁사업수익' }] },
        { code: '684', name: '신용보증부대수익', children: [{ code: '684-09', name: '기타신용보증부대수익' }] },
        {
          code: '685',
          name: '의료부대수익',
          children: [
            { code: '685-01', name: '장례식장운영수익' },
            { code: '685-09', name: '기타의료부대수익' },
          ],
        },
        { code: '688', name: '배당금수익', children: [{ code: '688-01', name: '배당금수익' }] },
      ],
    },
  ],
};

function mokChildrenFromShare() {
  return EXPENSE_MOK_SEMOK.map((m) => ({
    code: m.code,
    name: m.name,
    children: (m.children || []).map((s) => ({ code: s.code, name: s.name })),
  }));
}

/** 지출(사업비용) 트리 — 지침 2-1 + §4 목·세목
 * 지침상 「721 세항 사용 목 계상」 세항은 목을 중복 등록하지 않음(과목코드 유일성).
 * 공통 목·세목은 세항 721에만 펼침.
 */
const EXPENSE_TREE = {
  code: '700',
  name: '사업비용',
  children: [
    {
      code: '710',
      name: '영업비용',
      children: [
        { code: '721', name: '제품제조비', children: mokChildrenFromShare() },
        { code: '722', name: '대행사업비', ...SHARE_721, children: [] },
        { code: '723', name: '입장료원가', ...SHARE_721, children: [] },
        {
          code: '726',
          name: '상품매입비',
          children: [{ code: '233', name: '상품매입비', children: [{ code: '233-01', name: '상품매입비' }] }],
        },
        {
          code: '727',
          name: '신용보증사업비',
          children: [
            { code: '251', name: '재보증료', children: [{ code: '251-01', name: '재보증료' }] },
            { code: '252', name: '보증료환급', children: [{ code: '252-01', name: '보증료환급' }] },
            { code: '259', name: '기타신용보증비용', children: [{ code: '259-01', name: '기타신용보증비용' }] },
          ],
        },
        {
          code: '729',
          name: '기타매출원가',
          children: [{ code: '234', name: '관리사업원가', children: [{ code: '234-01', name: '관리사업원가' }] }],
        },
        {
          code: '738',
          name: '의료사업비',
          children: [
            { code: '261', name: '의료분쟁비용', children: [{ code: '261-01', name: '의료분쟁비용' }] },
            { code: '262', name: '의료피복침구비', children: [{ code: '262-01', name: '의료피복침구비' }] },
            { code: '263', name: '선교비', children: [{ code: '263-01', name: '선교비' }] },
            { code: '264', name: '의료사회사업비', children: [{ code: '264-01', name: '의료사회사업비' }] },
            { code: '265', name: '환경관리비', children: [{ code: '265-01', name: '환경관리비' }] },
            { code: '269', name: '기타의료비용', children: [{ code: '269-01', name: '기타의료비용' }] },
          ],
        },
        {
          code: '741',
          name: '용지및주택매출원가',
          children: [
            { code: '235', name: '택지매출원가', children: [{ code: '235-01', name: '택지매출원가' }] },
            { code: '237', name: '주택매출원가', children: [{ code: '237-01', name: '주택매출원가' }] },
            { code: '238', name: '기타용지등매출원가', children: [{ code: '238-01', name: '기타용지등매출원가' }] },
          ],
        },
        { code: '742', name: '임대사업원가', ...SHARE_721, children: [] },
        { code: '746', name: '목적사업비', ...SHARE_721, children: [] },
        { code: '753', name: '판매비와관리비', ...SHARE_721, children: [] },
        { code: '754', name: '일반관리비', ...SHARE_721, children: [] },
      ],
    },
    {
      code: '770',
      name: '영업외비용',
      children: [
        {
          code: '771',
          name: '지급이자및취급제비',
          children: [{ code: '311', name: '차입금이자상환', children: [{ code: '311-01', name: '차입금이자상환' }] }],
        },
        {
          code: '772',
          name: '유형자산처분손실',
          children: [{ code: '852', name: '유형자산처분손실', children: [{ code: '852-01', name: '유형자산처분손실' }] }],
        },
        {
          code: '773',
          name: '자산손상차손',
          children: [{ code: '855', name: '자산손상차손', children: [{ code: '855-01', name: '자산손상차손' }] }],
        },
        {
          code: '774',
          name: '외화환산손실',
          children: [{ code: '856', name: '외화환산손실', children: [{ code: '856-01', name: '외화환산손실' }] }],
        },
        {
          code: '775',
          name: '전기오류수정손실',
          children: [{ code: '857', name: '전기오류수정손실', children: [{ code: '857-01', name: '전기오류수정손실' }] }],
        },
        {
          code: '776',
          name: '투자자산처분손실',
          children: [{ code: '859', name: '투자자산처분손실', children: [{ code: '859-01', name: '투자자산처분손실' }] }],
        },
        {
          code: '779',
          name: '기타영업외비용',
          children: [
            { code: '318', name: '기부금', children: [{ code: '318-01', name: '기부금' }] },
            { code: '320', name: '기타협의체등부담금', children: [{ code: '320-01', name: '기타협의체등부담금' }] },
            { code: '851', name: '반환금', children: [{ code: '851-01', name: '반환금' }] },
            { code: '871', name: '기타영업외비용', children: [{ code: '871-01', name: '기타영업외비용' }] },
          ],
        },
        { code: '783', name: '수탁사업비용', ...SHARE_721, children: [] },
        { code: '784', name: '신용보증부대사업비', ...SHARE_721, children: [] },
        { code: '785', name: '의료부대비용', ...SHARE_721, children: [] },
        { code: '786', name: '수탁진료사업비', ...SHARE_721, children: [] },
      ],
    },
    {
      code: '790',
      name: '법인세등',
      children: [
        {
          code: '791',
          name: '법인세등',
          children: [{ code: '891', name: '법인세등', children: [{ code: '891-01', name: '법인세등' }] }],
        },
      ],
    },
    {
      code: '800',
      name: '예비비',
      remark: '사업예산 예비비 — 자본예산 예비비와 동일 코드(800·801), 예산구분(사업예산)으로 식별',
      children: [
        {
          code: '801',
          name: '예비비',
          children: [{ code: '809', name: '예비비', children: [{ code: '809-01', name: '예비비' }] }],
        },
      ],
    },
  ],
};

/** 자본예산 수입 — 관 100대 */
const CAPITAL_INCOME_TREE = {
  code: '100',
  name: '자본적수입',
  children: [
    {
      code: '110',
      name: '출자금',
      children: [
        {
          code: '111',
          name: '정부출자금',
          children: [
            { code: '111-01', name: '국고출자금' },
            { code: '111-02', name: '자치단체출자금' },
          ],
        },
        { code: '119', name: '기타출자금', children: [{ code: '119-01', name: '기타출자금' }] },
      ],
    },
    {
      code: '120',
      name: '보조금',
      children: [
        {
          code: '121',
          name: '자본적보조금',
          children: [
            { code: '121-01', name: '국고보조금' },
            { code: '121-02', name: '자치단체보조금' },
            { code: '121-09', name: '기타보조금' },
          ],
        },
      ],
    },
    {
      code: '130',
      name: '차입금',
      children: [
        {
          code: '131',
          name: '장기차입금',
          children: [
            { code: '131-01', name: '금융기관차입금' },
            { code: '131-02', name: '공사채' },
            { code: '131-09', name: '기타장기차입금' },
          ],
        },
      ],
    },
    {
      code: '140',
      name: '자산매각대금',
      children: [
        {
          code: '141',
          name: '유형자산매각대금',
          children: [
            { code: '141-01', name: '토지매각대금' },
            { code: '141-02', name: '건물매각대금' },
            { code: '141-09', name: '기타유형자산매각대금' },
          ],
        },
        { code: '142', name: '투자자산매각대금', children: [{ code: '142-01', name: '투자자산매각대금' }] },
      ],
    },
    {
      code: '150',
      name: '전입금',
      children: [{ code: '151', name: '사업예산전입금', children: [{ code: '151-01', name: '사업예산전입금' }] }],
    },
    {
      code: '160',
      name: '기타자본적수입',
      children: [
        { code: '161', name: '기타자본적수입', children: [{ code: '161-01', name: '기타자본적수입' }] },
      ],
    },
  ],
};

/** 자본예산 지출 — 관 200대 (+ 자본 예비비: 사업예산과 동일 코드 800·801) */
const CAPITAL_EXPENSE_TREE = {
  code: '200',
  name: '자본적지출',
  children: [
    {
      code: '210',
      name: '유형자산취득',
      children: [
        { code: '211', name: '토지', children: [{ code: '420', name: '토지', children: [{ code: '420-01', name: '토지' }] }] },
        { code: '212', name: '건물', children: [{ code: '421', name: '건물', children: [{ code: '421-01', name: '건물' }] }] },
        {
          code: '213',
          name: '구축물',
          children: [{ code: '422', name: '구축물', children: [{ code: '422-01', name: '구축물' }] }],
        },
        {
          code: '214',
          name: '기계장치',
          children: [{ code: '423', name: '기계장치', children: [{ code: '423-01', name: '기계장치' }] }],
        },
        {
          code: '215',
          name: '차량운반구',
          children: [{ code: '424', name: '차량운반구', children: [{ code: '424-01', name: '차량운반구' }] }],
        },
        {
          code: '216',
          name: '공구와기구및비품',
          children: [{ code: '425', name: '공구와기구및비품', children: [{ code: '425-01', name: '공구와기구및비품' }] }],
        },
      ],
    },
    {
      code: '220',
      name: '건설중인자산',
      children: [
        {
          code: '221',
          name: '건설중인자산',
          children: [{ code: '426', name: '건설중인자산', children: [{ code: '426-01', name: '건설중인자산' }] }],
        },
      ],
    },
    {
      code: '230',
      name: '투자자산',
      children: [
        {
          code: '231',
          name: '투자자산',
          children: [{ code: '430', name: '투자자산', children: [{ code: '430-01', name: '투자자산' }] }],
        },
      ],
    },
    {
      code: '240',
      name: '무형자산',
      children: [
        {
          code: '241',
          name: '무형자산',
          children: [{ code: '440', name: '무형자산', children: [{ code: '440-01', name: '무형자산' }] }],
        },
      ],
    },
    {
      code: '250',
      name: '기타자본적지출',
      children: [
        {
          code: '251',
          name: '기타자본적지출',
          children: [{ code: '450', name: '기타자본적지출', children: [{ code: '450-01', name: '기타자본적지출' }] }],
        },
      ],
    },
    {
      code: '260',
      name: '차입금상환',
      children: [
        {
          code: '261',
          name: '원금상환',
          children: [{ code: '460', name: '차입금원금상환', children: [{ code: '460-01', name: '차입금원금상환' }] }],
        },
      ],
    },
    {
      code: '800',
      name: '예비비',
      remark: '자본예산 예비비 — 사업예산 예비비와 동일 코드, 예산구분(자본예산)으로 식별',
      children: [
        {
          code: '801',
          name: '예비비',
          children: [{ code: '809', name: '예비비', children: [{ code: '809-01', name: '예비비' }] }],
        },
      ],
    },
  ],
};

function pickSubjectTree(inoutDiv, budgetType) {
  const isCapital = budgetType === '자본예산';
  if (inoutDiv === '수입') return isCapital ? CAPITAL_INCOME_TREE : INCOME_TREE;
  return isCapital ? CAPITAL_EXPENSE_TREE : EXPENSE_TREE;
}

/**
 * 트리를 단일테이블 과목 행으로 펼침
 * depth → 레벨명 (usedLevels에 맞게 매핑)
 */
export function flattenSubjectHierarchy(tree, levelNames = []) {
  const out = [];
  let sort = 0;

  const walk = (node, parentCode, depth) => {
    if (!node?.code) return;
    const levelName = levelNames[depth] || levelNames[levelNames.length - 1] || `LV${depth + 1}`;
    const isLeaf = !node.children?.length;
    const finalLevel = levelNames[levelNames.length - 1];
    // 세목·목의 `101-01` → `10101` 표기
    const subjectCode = normalizeMokSemokCode(String(node.code));
    sort += 10;
    out.push({
      ...ATTR,
      levelName,
      parentCode: parentCode || '',
      subjectCode,
      subjectName: node.name || subjectCode,
      abbrName: (node.name || '').slice(0, 8),
      finalYn: isLeaf && levelName === finalLevel ? 'Y' : isLeaf ? 'Y' : 'N',
      sortOrder: sort,
      remark: node.remark || (node.shareMokFrom ? `721 세항 사용 목 계상` : ATTR.remark),
    });
    (node.children || []).forEach((ch) => walk(ch, subjectCode, depth + 1));
  };

  walk(tree, '', 0);
  return out;
}

/**
 * 확정된 usedLevels에 맞춰 행의 levelName·finalYn 재배치
 * (레벨 수가 다르면 깊이를 압축/확장)
 */
export function mapRowsToUsedLevels(flatRows, usedLevels = []) {
  if (!usedLevels?.length) return flatRows;
  const names = usedLevels.map((l) => l.levelName);
  const finalName = usedLevels.find((l) => l.finalYn === 'Y')?.levelName || names[names.length - 1];

  // 원본 깊이: 코드 자릿수/하이픈으로 대략 구분하지 말고, parent 체인으로 깊이 계산
  const byCode = new Map(flatRows.map((r) => [r.subjectCode, r]));
  const depthOf = (code, guard = 0) => {
    if (!code || guard > 20) return 0;
    const row = byCode.get(code);
    if (!row?.parentCode) return 0;
    return 1 + depthOf(row.parentCode, guard + 1);
  };

  return flatRows.map((r) => {
    const d = depthOf(r.subjectCode);
    const maxIdx = names.length - 1;
    const idx = Math.min(d, maxIdx);
    const levelName = names[idx];
    const childrenExist = flatRows.some((x) => x.parentCode === r.subjectCode);
    const finalYn = !childrenExist && levelName === finalName ? 'Y' : !childrenExist ? 'Y' : 'N';
    return { ...r, levelName, finalYn };
  });
}

export function getMofas2026SubjectSeed(inoutDiv, usedLevels, budgetType = STRUCTURE_BUDGET_TYPE) {
  const tree = pickSubjectTree(inoutDiv, budgetType);
  const defaultLevels =
    inoutDiv === '수입'
      ? ['관', '항', '세항', '목']
      : ['관', '항', '세항', '목', '세목'];
  const levelNames = usedLevels?.length ? usedLevels.map((l) => l.levelName) : defaultLevels;
  const flat = flattenSubjectHierarchy(tree, levelNames);
  const mapped = mapRowsToUsedLevels(
    flat,
    usedLevels?.length
      ? usedLevels
      : levelNames.map((levelName, i) => ({
          levelName,
          finalYn: i === levelNames.length - 1 ? 'Y' : 'N',
        }))
  );
  const bt = budgetType || STRUCTURE_BUDGET_TYPE;
  return ensureSubjectsIdentity(
    mapped.map((r) => ({
      ...r,
      budgetType: bt,
      remark: r.remark || `${MOFAS_2026_SOURCE} · ${bt}`,
    }))
  );
}

/**
 * 구조 미확정·과목 미등록 시 2026 행안부 사업예산 시드를 확정·적재
 * (과목구조는 예산구분 없이 1건, 과목은 예산구분별로 적재)
 */
export async function ensureMofas2026Bootstrap(dims, { userId } = {}) {
  const types = resolveBudgetTypes(dims.budgetType);
  let seeded = false;
  let seedCount = 0;
  const allSubjects = [];

  const structureDims = {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    inoutDiv: dims.inoutDiv,
    purpose: dims.purpose || '집행',
  };

  let confirmed = getConfirmedStructure(structureDims);
  if (!confirmed) {
    const rows = clonePreset(structureDims.inoutDiv, structureDims.purpose);
    const v = validateStructure(rows);
    if (!v.canConfirm) {
      return { ok: false, reason: '시드 구조를 확정할 수 없습니다.', seedCount: 0 };
    }
    const fy = String(structureDims.fiscalYear || new Date().getFullYear());
    const prev = await loadStructurePayload(structureDims);
    await saveStructurePayload(structureDims, {
      dims: structureDims,
      rows,
      status: '확정',
      purpose: structureDims.purpose,
      usedLevels: v.used,
      finalLevelName: v.finalLevelName,
      acctMapLevelNames: v.acctMapLevelNames,
      previewText: v.previewText,
      versionNo: (Number(prev?.versionNo) || 0) + 1 || 1,
      confirmedAt: new Date().toISOString(),
      confirmedBy: userId || 'MOFAS-2026-SEED',
      startDate: `${fy}-01-01`,
      endDate: `${fy}-12-31`,
      remark: MOFAS_2026_SOURCE,
      savedAt: new Date().toISOString(),
      savedBy: userId || 'MOFAS-2026-SEED',
    });
    confirmed = getConfirmedStructure(structureDims);
  }

  if (!confirmed) {
    return { ok: false, reason: '시드 구조를 확정할 수 없습니다.', seedCount: 0 };
  }

  for (const budgetType of types) {
    const d = { ...dims, budgetType, purpose: structureDims.purpose };
    let subjects = await loadSubjects(d);
    const isLegacyDemo =
      subjects.length > 0 &&
      subjects.length <= 8 &&
      subjects.some((r) => r.subjectCode === '100' && r.subjectName === '일반관리');
    if ((!subjects.length || isLegacyDemo) && confirmed) {
      subjects = getMofas2026SubjectSeed(d.inoutDiv, confirmed.usedLevels, budgetType);
      await saveSubjects(d, subjects);
      seeded = true;
    }
    seedCount += subjects.length;
    subjects.forEach((r) => allSubjects.push({ ...r, budgetType: r.budgetType || budgetType }));
  }

  return {
    ok: true,
    structure: confirmed,
    subjects: allSubjects,
    seedCount,
    seeded,
    source: MOFAS_2026_SOURCE,
  };
}
